import { Dispatch } from 'redux'

import { Contrato, ContratoSelecionado, Matricula } from '@store/refinanciamento/contratos/types'
import * as actions from '@store/refinanciamento/contratos/actions'
import { ConveniosMultiContratosBloqueado } from '@utils/enums'
import Tag from '@utils/Tag'
import { getConvenioName } from '@utils/functions'

import {
  dialogVariants,
  VariantDialogContratos,
} from './Bottomsheets/BottomsheetCustom/dialogVariants'

export const limparDadosContrato = (dispatch: Dispatch) => {
  dispatch(actions.setMatriculaSelecionada(''))
  dispatch(actions.setConvenioSelecionado(''))
  dispatch(actions.setConvenioCodigoSelecionado(0))
  dispatch(actions.setOrgaoSelecionado(''))
  dispatch(actions.setOrgaoCodigoSelecionado(0))
  dispatch(actions.setCodProduto(0))
}

export const atualizarDadosContrato = (dispatch: Dispatch, contrato: ContratoSelecionado) => {
  dispatch(actions.setConvenioSelecionado(contrato.convenioDesc || ''))
  dispatch(actions.setConvenioCodigoSelecionado(contrato.convenioId))
  dispatch(actions.setOrgaoSelecionado(contrato.orgaoDesc || ''))
  dispatch(actions.setOrgaoCodigoSelecionado(contrato.orgaoId))
  dispatch(actions.setMatriculaSelecionada(contrato.numeroMatricula || ''))
  dispatch(actions.setCodProduto(contrato.codProduto))
  dispatch(actions.setConvenioName(getConvenioName(contrato.convenioId)))
}

export const isContratoElegivel = (contrato: Contrato): boolean => !contrato.indisponivel

export const getContratosElegiveis = (listaMatriculas: Matricula) =>
  listaMatriculas.listContrato.filter(isContratoElegivel)

export const disableContinue = (contratosSelecionados: ContratoSelecionado[] | null) =>
  !contratosSelecionados?.length

export const hasContratoSelecionado = (
  contrato: string,
  contratosSelecionados: ContratoSelecionado[] | null,
) => contratosSelecionados?.some((item) => item.numeroContrato === contrato)

const isConvenioSelecaoUnica = (convenioDesc?: string): boolean => {
  const desc = convenioDesc?.toUpperCase()
  return Boolean(desc?.includes('CTPS') || desc?.includes('INSS'))
}

export const hasContratoSelecaoUnica = (contratosSelecionados: ContratoSelecionado[] | null) =>
  Boolean(contratosSelecionados?.some((item) => isConvenioSelecaoUnica(item.convenioDesc)))

export const disableMultiContratos = (
  contrato: Contrato,
  contratosSelecionados: ContratoSelecionado[] | null,
) => {
  if (!contratosSelecionados?.length) {
    return false
  }

  if (
    hasContratoSelecaoUnica(contratosSelecionados) ||
    isConvenioSelecaoUnica(contrato.convenioDesc)
  ) {
    return contratosSelecionados[0]?.numeroContrato !== contrato.numeroContrato
  }

  return false
}

export const disabledCheckbox = (
  numeroMatricula: string,
  contrato: Contrato,
  matriculaSelecionada: string,
  contratosSelecionados: ContratoSelecionado[] | null,
) =>
  !(numeroMatricula === matriculaSelecionada || matriculaSelecionada === '') ||
  disableMultiContratos(contrato, contratosSelecionados)

export const verificarSelecaoPorMatricula = (
  listaMatriculas: Matricula,
  contratosSelecionados: ContratoSelecionado[] | null,
) => {
  const contratosElegiveis = getContratosElegiveis(listaMatriculas)

  if (contratosElegiveis.length === 0) return false

  return contratosElegiveis.every((contrato) =>
    contratosSelecionados?.some(
      (contratoSelecionado) =>
        contrato.numeroContrato === contratoSelecionado.numeroContrato &&
        contratoSelecionado.numeroMatricula === listaMatriculas.numeroMatricula,
    ),
  )
}

export const temContratosElegiveis = (listaMatriculas: Matricula) => {
  const contratosElegiveis = getContratosElegiveis(listaMatriculas)
  return contratosElegiveis.length > 0
}

export const hasContratoInss = (contratosSelecionados: ContratoSelecionado[] | null) =>
  contratosSelecionados?.some(
    (item) => item.convenioDesc && item.convenioDesc.toUpperCase().includes('INSS'),
  )

export const hasContratoCtps = (contratosSelecionados: ContratoSelecionado[] | null) =>
  contratosSelecionados?.some(
    (item) => item.convenioDesc && item.convenioDesc.toUpperCase().includes('CTPS'),
  )

export const isConvenioClt = (descricaoConvenio?: string): boolean =>
  Boolean(descricaoConvenio?.toUpperCase().includes('CLT'))

interface ContratosHelpers {
  dispatch: Dispatch
  contratosSelecionados: ContratoSelecionado[] | null
  matriculaSelecionada: string
  convenioCodigo: number
  isApp: boolean
  setVariantDialog: (variant: VariantDialogContratos) => void
  setShowBottomSheet: (show: boolean) => void
}

export const createContratosHelpers = ({
  dispatch,
  contratosSelecionados,
  matriculaSelecionada,
  isApp,
  setVariantDialog,
  setShowBottomSheet,
}: ContratosHelpers) => {
  const limparDadosContratoInterno = () => limparDadosContrato(dispatch)
  const atualizarDadosContratoInterno = (contrato: ContratoSelecionado) =>
    atualizarDadosContrato(dispatch, contrato)
  const adicionaContrato = (contrato: ContratoSelecionado) => {
    if (
      (hasContratoSelecaoUnica(contratosSelecionados) ||
        isConvenioSelecaoUnica(contrato.convenioDesc)) &&
      contratosSelecionados &&
      contratosSelecionados.length > 0
    ) {
      setVariantDialog(dialogVariants.SelecaoUnica)
      setShowBottomSheet(true)
      return
    }

    if (
      ConveniosMultiContratosBloqueado.indexOf(contrato.convenioDesc) >= 0 &&
      contratosSelecionados &&
      contratosSelecionados.length > 0
    ) {
      setVariantDialog(dialogVariants.SelecaoUnica)
      setShowBottomSheet(true)
      return
    }

    if (contratosSelecionados && contratosSelecionados.length > 0) {
      const primeiroContrato = contratosSelecionados[0]
      if (primeiroContrato.convenioDesc !== contrato.convenioDesc) {
        setVariantDialog(dialogVariants.SelecaoContratos)
        setShowBottomSheet(true)
        return
      }
    }

    dispatch(actions.setContratosSelecionados([...(contratosSelecionados || []), contrato]))
    atualizarDadosContratoInterno(contrato)
  }

  const removeContrato = (contrato: string) => {
    const novaLista = contratosSelecionados?.filter((item) => item.numeroContrato !== contrato)
    if (!novaLista || novaLista.length === 0) {
      limparDadosContratoInterno()
    } else {
      atualizarDadosContratoInterno(novaLista[0])
    }
    dispatch(actions.setContratosSelecionados(novaLista || []))
  }

  const selecionarTodos = (listaMatriculas: Matricula) => {
    if (hasContratoSelecaoUnica(contratosSelecionados)) {
      setVariantDialog(dialogVariants.SelecaoUnica)
      setShowBottomSheet(true)
      return
    }

    const hasSelecaoUnicaInList = listaMatriculas.listContrato.some((contrato) =>
      isConvenioSelecaoUnica(contrato.convenioDesc),
    )

    if (hasSelecaoUnicaInList) {
      const primeiroContratoSelecaoUnica = listaMatriculas.listContrato.find(
        (contrato) => isContratoElegivel(contrato) && isConvenioSelecaoUnica(contrato.convenioDesc),
      )

      if (primeiroContratoSelecaoUnica) {
        const contratoSelecionado: ContratoSelecionado = {
          ...primeiroContratoSelecaoUnica,
          numeroMatricula: listaMatriculas.numeroMatricula,
        }
        dispatch(actions.setContratosSelecionados([contratoSelecionado]))
        atualizarDadosContratoInterno(contratoSelecionado)
      }
      return
    }

    const contratosSelecionadosTemp: ContratoSelecionado[] = []

    listaMatriculas.listContrato.forEach((contrato) => {
      if (isContratoElegivel(contrato)) {
        const contratoJaAdicionado = contratosSelecionados?.some(
          (c) => c.numeroContrato === contrato.numeroContrato,
        )

        if (!contratoJaAdicionado) {
          contratosSelecionadosTemp.push({
            ...contrato,
            numeroMatricula: listaMatriculas.numeroMatricula,
          })
        }
      }
    })

    if (contratosSelecionadosTemp.length > 0) {
      dispatch(
        actions.setContratosSelecionados([
          ...(contratosSelecionados || []),
          ...contratosSelecionadosTemp,
        ]),
      )
      atualizarDadosContratoInterno(contratosSelecionadosTemp[0])
    }
  }

  const handleContratoChange = (contrato: Contrato, numeroMatricula: string) => {
    if (disabledCheckbox(numeroMatricula, contrato, matriculaSelecionada, contratosSelecionados)) {
      setVariantDialog(
        hasContratoSelecaoUnica(contratosSelecionados)
          ? dialogVariants.SelecaoUnica
          : dialogVariants.SelecaoContratos,
      )
      setShowBottomSheet(true)
      return
    }

    if (!isContratoElegivel(contrato)) {
      setVariantDialog(dialogVariants.Indisponivel)
      setShowBottomSheet(true)
      return
    }

    if (hasContratoSelecionado(contrato.numeroContrato, contratosSelecionados)) {
      removeContrato(contrato.numeroContrato)
    } else {
      adicionaContrato({ ...contrato, numeroMatricula })
      atualizarDadosContratoInterno({ ...contrato, numeroMatricula })
    }
  }

  const handleSelecionarTodos = (listaMatriculas: Matricula) => {
    if (isApp) {
      Tag.enviarTagueamentoApp({
        screen: 'clt:refin:selection',
        ref_type: 'botao',
        content_action: 'Toque',
        action_id: 'Selecionar todos contratos',
        section: 'selection',
        sub_section: '',
        step: '',
        scenario: 'Seleção de contratos',
        ref_figma: '2',
      })
    } else {
      Tag.enviarTagueamentoWeb({
        element_action: 'click button',
        element_name: 'Selecionar todos contratos',
        c_page: window.location.href,
        redirect_url: '',
        section_name: 'payrollloan:refin:contracts',
        section: 'contracts',
        sub_section: '',
        step: '',
        scenario: 'Seleção de contratos',
      })
    }

    if (verificarSelecaoPorMatricula(listaMatriculas, contratosSelecionados)) {
      const contratosDaMatricula =
        contratosSelecionados?.filter(
          (contrato) => contrato.numeroMatricula !== listaMatriculas.numeroMatricula,
        ) || []
      dispatch(actions.setContratosSelecionados(contratosDaMatricula))

      if (contratosDaMatricula.length === 0) {
        limparDadosContratoInterno()
      } else {
        atualizarDadosContratoInterno(contratosDaMatricula[0])
      }
    } else {
      selecionarTodos(listaMatriculas)
    }
  }

  const handleContratoClick = (numeroMatricula: string, contrato: Contrato) => {
    if (disabledCheckbox(numeroMatricula, contrato, matriculaSelecionada, contratosSelecionados)) {
      setVariantDialog(
        hasContratoSelecaoUnica(contratosSelecionados)
          ? dialogVariants.SelecaoUnica
          : dialogVariants.SelecaoContratos,
      )
      setShowBottomSheet(true)
    } else if (!isContratoElegivel(contrato)) {
      setVariantDialog(dialogVariants.Indisponivel)
      setShowBottomSheet(true)
    }
  }

  return {
    handleContratoChange,
    handleSelecionarTodos,
    handleContratoClick,
  }
}
